<template>

  <div class="grid grid-cols-6 xl:grid-cols-4 gap-4 max-w-7xl mx-auto px-6 py-6">
    <div class="col-span-full">
      <Button data-cy="refresh-all-button" severity="secondary" @click="refreshAll(true)" :label="$t('common.refresh_all')"/>
    </div>

    <DeviceDashboardCard
        class="col-span-3 md:col-span-2 xl:col-span-1"
        :value="devicesDashboardPageStore?.getDevicesTotalCount"
        :loading="devicesDashboardPageStore?.getDevicesTotalCountLoading"
    />

    <DeviceDashboardCard
        :subtitle="$t('pages.devices.dashboard.up_to_date')"
        class="col-span-3 md:col-span-2 xl:col-span-1"
        :value="devicesDashboardPageStore?.getUpToDateDevicesCount"
        :loading="devicesDashboardPageStore?.getUpToDateDevicesCountLoading"
    />

    <DevicesList
        :value="devicesDashboardPageStore?.getDevicesRecentlyUpdated"
        :loading="devicesDashboardPageStore?.getDevicesRecentlyUpdatedLoading"
        :first="devicesDashboardPageStore?.getFirstPage('recently_updated_devices')"
        :rows="devicesDashboardPageStore.recently_updated_devices.params.per_page"
        @page="$event => devicesDashboardPageStore.onPage($event)"
        :total-records="devicesDashboardPageStore?.recently_updated_devices.totalRecords"
        :subtitle="$t('pages.devices.dashboard.recently_updated')"
        class="row-start-5 md:row-start-4 col-span-3 xl:row-start-auto xl:row-span-2 xl:col-span-1 h-[28rem]"
    >
      <template v-slot:item="{ item, remove }">
        <router-link :to="{name: 'devices.show', params: { device: item.id }}"
                     class="truncate flex flex-1 space-x-3 items-center py-1 -mx-1">
          <div class="h-9 w-9 bg-primary-50 rounded-md inline-flex items-center justify-center">
            <IconRouter class="text-primary-900 opacity-50"/>
          </div>
          <div class="flex-1 flex flex-col justify-start overflow-x-hidden">
            <span class="text-sm font-medium text-surface-600">{{ item.serial_number }}</span>
            <span class="text-xs font-medium text-surface-400 flex-1">
                {{ item.current_firmware?.version_number }} -> {{ item.target_firmware?.version_number }}
              </span>
            <span class="text-xxs font-medium text-surface-400 mt-1.5">
                Last update: {{ item.firmware_last_updated_at }}
              </span>
          </div>
        </router-link>
      </template>
    </DevicesList>

    <DevicesList
        :value="devicesDashboardPageStore?.getDevicesNeedingAnUpdate"
        :loading="devicesDashboardPageStore?.getDevicesNeedingAnUpdateLoading"
        :first="devicesDashboardPageStore?.getFirstPage('needing_an_update')"
        :rows="devicesDashboardPageStore.needing_an_update.params.per_page"
        @page="$event => devicesDashboardPageStore.onPage($event)"
        :total-records="devicesDashboardPageStore?.needing_an_update.totalRecords"
        :subtitle="$t('pages.devices.dashboard.awaiting_an_update')"
        class="row-start-5 md:row-start-4 col-span-3 xl:row-start-auto xl:row-span-2 xl:col-span-1 h-[28rem]"
    >
      <template v-slot:item="{ item, remove }">
        <router-link :to="{name: 'devices.show', params: { device: item.id }}"
                     class="truncate flex flex-1 space-x-3 items-center py-1 -mx-1">
          <div class="h-9 w-9 bg-primary-50 rounded-md inline-flex items-center justify-center">
            <IconRouter class="text-primary-900 opacity-50"/>
          </div>
          <div class="flex-1 flex flex-col justify-start overflow-x-hidden">
            <span class="text-sm font-medium text-surface-600">{{ item.serial_number }}</span>
            <span class="text-xs font-medium text-surface-400 flex-1">
                {{ item.current_firmware?.version_number }} -> {{ item.target_firmware?.version_number }}
              </span>
            <span class="text-xxs font-medium text-surface-400 mt-1.5">
                Last update: {{ item.firmware_last_updated_at }}
            </span>
          </div>
        </router-link>
      </template>
    </DevicesList>

    <DevicesByDeviceType
        :data="devicesDashboardPageStore?.getDevicesByDeviceType"
        :loading="devicesDashboardPageStore?.getDevicesByDeviceTypeLoading"
        class="row-start-2 col-start-1 col-span-3 xl:row-start-auto xl:col-start-auto xl:col-span-1"
    />

    <DeviceByUpdateStatus
        :data="devicesDashboardPageStore?.getDevicesByUpdateStatus"
        :loading="devicesDashboardPageStore?.getDevicesByUpdateStatusLoading"
        class="row-start-2 col-start-4 col-span-3 xl:row-start-auto xl:col-start-auto xl:col-span-1"
    />

    <DevicesOverTime
        :data="devicesDashboardPageStore?.getDeviceCountOverTime"
        :loading="devicesDashboardPageStore?.getDeviceCountOverTimeLoading"
        y-axis-key="cumulative_total"
        class="col-span-6 row-start-3 md:col-span-3 xl:row-start-auto xl:col-span-2"
    />

    <DevicesOverTime
        :data="devicesDashboardPageStore?.getDeviceUpdateCountOverTime"
        :loading="devicesDashboardPageStore?.getDeviceUpdateCountOverTimeLoading"
        :subtitle="$t('pages.devices.dashboard.updates_by_date')"
        y-axis-key="total"
        class="row-start-4 col-span-6 md:row-start-3 md:col-span-3 xl:row-start-auto xl:col-span-2"/>

  </div>
</template>

<script>
import DeviceDashboardCard from "@/Widgets/Cards/DeviceDashboardCard.vue";
import DevicesList from "@/Widgets/Lists/DevicesList.vue";
import {mapStores} from "pinia";
import {useDevicesDashboardPageStore} from "@/stores/pages/devicesDashboardPage.js";
import {IconRouter} from "@tabler/icons-vue";
import DevicesByDeviceType from "@/Widgets/Charts/DevicesByDeviceType.vue";
import DeviceByUpdateStatus from "@/Widgets/Charts/DevicesByUpdateStatus.vue";
import AssetsOverTime from "@/Widgets/Charts/AssetsOverTime.vue";
import DevicesOverTime from "@/Widgets/Charts/DevicesOverTime.vue";
import AssetsList from "@/Widgets/Lists/AssetsList.vue";

export default {
  name: "DevicesDashboard",
  components: {
    AssetsList,
    DevicesOverTime,
    AssetsOverTime,
    DeviceByUpdateStatus,
    DevicesByDeviceType,
    DevicesList,
    DeviceDashboardCard,
    IconRouter
  },
  computed: {
    ...mapStores(useDevicesDashboardPageStore),
  },
  methods: {
    refreshAll(forceRefresh = false) {
      try {
        this.devicesDashboardPageStore.fetchDevicesTotalCount({}, forceRefresh);
        this.devicesDashboardPageStore.fetchUpToDateDevicesCount({}, forceRefresh);
        this.devicesDashboardPageStore.fetchDevicesByDeviceType({}, forceRefresh);
        this.devicesDashboardPageStore.fetchDevicesByUpdateStatus({}, forceRefresh);
        this.devicesDashboardPageStore.fetchDeviceCountOverTime({}, forceRefresh);
        this.devicesDashboardPageStore.fetchDeviceUpdateCountOverTime({}, forceRefresh);
        this.devicesDashboardPageStore.fetchDevicesRecentlyUpdated({}, forceRefresh);
        this.devicesDashboardPageStore.fetchDevicesNeedingAnUpdate({}, forceRefresh);
      } catch (e) {
        console.log(e);
      }
    }
  },
  mounted() {
    this.refreshAll();
  }
}
</script>

<style scoped>

</style>
